// ==============================================
// Compare plans: every live plan side by side, one column each, in the
// sections of planSections.ts. Switches can be changed in the grid; one
// review covers every plan changed, and each plan is then saved (and kept in
// its own history) with the same optional reason.
// ==============================================

import { useEffect, useMemo, useState } from 'react';
import type { Package } from '../../../types/subscription';
import { InfoTip } from './InfoTip';
import { emptyPackageForm, packageToForm, type PackageFormData } from './packageForm';
import { changedFields, showValue, type FieldChange } from './planChanges';
import { PLAN_SECTIONS, fieldsOf, type PlanField } from './planSections';

export interface PlanSaveItem { id: string; name: string; data: PackageFormData; reason?: string }
export interface PlanSaveResult { id: string; ok: boolean; error?: string }

/** A single on/off setting, which the grid shows as a switch (Scout diagnostics is one, with a custom layout in the editor). */
const isSwitch = (f: PlanField) => f.keys.length === 1 && typeof emptyPackageForm[f.keys[0]] === 'boolean';

/** Settings that read well in a grid cell; text, lists and the token block stay in the editor. */
const inGrid = (f: PlanField) => isSwitch(f) || !['text', 'textarea', 'slug', 'custom'].includes(f.kind);

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function PlanCompareGrid({
  packages,
  subscriberCounts,
  saveChanges,
  onOpen,
}: {
  packages: Package[];
  /** Active subscriptions per plan id; unknown while loading. */
  subscriberCounts?: Record<string, number>;
  saveChanges: (items: PlanSaveItem[]) => Promise<PlanSaveResult[]>;
  /** Open a plan in the full editor. */
  onOpen: (pkg: Package) => void;
}) {
  const plans = useMemo(
    () => packages.filter((p) => p.visibility !== 'archived').sort((a, b) => a.sort_order - b.sort_order),
    [packages],
  );
  const [base, setBase] = useState<Record<string, PackageFormData>>({});
  const [edits, setEdits] = useState<Record<string, PackageFormData>>({});
  const [reviewing, setReviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [failures, setFailures] = useState<string[]>([]);

  // A fresh list from the server is the new starting point for every plan.
  useEffect(() => {
    setBase(Object.fromEntries(packages.map((p) => [p.id, packageToForm(p)])));
  }, [packages]);

  const formOf = (id: string) => edits[id] ?? base[id];
  const changesByPlan = useMemo(
    () => plans
      .map((p) => ({ plan: p, changes: edits[p.id] && base[p.id] ? changedFields(base[p.id], edits[p.id]) : [] as FieldChange[] }))
      .filter((c) => c.changes.length > 0),
    [plans, base, edits],
  );
  const changeCount = changesByPlan.reduce((n, c) => n + c.changes.length, 0);

  useEffect(() => {
    if (changeCount === 0) setReviewing(false);
  }, [changeCount]);

  const toggle = (id: string, key: keyof PackageFormData) => {
    const next = { ...formOf(id), [key]: !formOf(id)[key] };
    setEdits((e) => {
      const rest = { ...e };
      if (JSON.stringify(next) === JSON.stringify(base[id])) delete rest[id];
      else rest[id] = next;
      return rest;
    });
  };

  const save = async () => {
    setSaving(true);
    const items = changesByPlan.map(({ plan }) => ({
      id: plan.id, name: plan.name, data: edits[plan.id], reason: reason.trim() || undefined,
    }));
    const results = await saveChanges(items);
    const saved = new Set(results.filter((r) => r.ok).map((r) => r.id));
    setBase((b) => ({ ...b, ...Object.fromEntries(items.filter((i) => saved.has(i.id)).map((i) => [i.id, i.data])) }));
    setEdits((e) => Object.fromEntries(Object.entries(e).filter(([id]) => !saved.has(id))));
    setFailures(results.filter((r) => !r.ok).map((r) => {
      const name = items.find((i) => i.id === r.id)?.name ?? r.id;
      return `${name} was not saved: ${r.error ?? 'unknown error'}`;
    }));
    setReviewing(false);
    setSaving(false);
    if (saved.size === items.length) setReason('');
  };

  if (plans.length === 0 || Object.keys(base).length === 0) {
    return <p className="text-[15px] text-slate-300">No live plans to compare.</p>;
  }

  return (
    <div className="space-y-6">
      {failures.length > 0 && (
        <div role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-[15px] text-red-200">
          {failures.map((f) => <p key={f}>{f}</p>)}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-700 bg-slate-800/50">
        <table className="w-full min-w-[40rem] text-[15px]">
          <thead>
            <tr className="border-b border-slate-700">
              <th scope="col" className="sticky left-0 z-10 bg-slate-800 px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-slate-400">
                Setting
              </th>
              {plans.map((p) => (
                <th key={p.id} scope="col" className="px-4 py-3 text-center">
                  <button type="button" aria-label={`Open ${p.name}`} onClick={() => onOpen(p)}
                    className="text-base font-semibold text-white hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 rounded">
                    {p.name}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PLAN_SECTIONS.map((s) => {
              const fields = fieldsOf(s).filter(inGrid);
              if (fields.length === 0) return null;
              return [
                <tr key={s.id} className="bg-slate-900/40">
                  <th scope="row" colSpan={plans.length + 1}
                    className={`sticky left-0 px-4 pt-5 pb-2 text-left text-sm font-semibold uppercase tracking-wide ${s.danger ? 'text-red-300' : 'text-slate-400'}`}>
                    {s.title}
                  </th>
                </tr>,
                ...fields.map((f) => (
                  <tr key={f.keys[0]} className="border-t border-slate-700/60">
                    <th scope="row" className="sticky left-0 z-10 bg-slate-800 px-4 py-2 text-left font-normal text-slate-100">
                      <span className="inline-flex items-center gap-2">{f.label}<InfoTip label={f.label}>{f.info}</InfoTip></span>
                    </th>
                    {plans.map((p) => {
                      const form = formOf(p.id);
                      const key = f.keys[0];
                      const changed = edits[p.id] && JSON.stringify(edits[p.id][key]) !== JSON.stringify(base[p.id][key]);
                      return (
                        <td key={p.id} className={`px-4 py-2 text-center ${changed ? 'bg-cyan-500/10' : ''}`}>
                          {isSwitch(f) ? (
                            <button type="button" role="switch" aria-checked={!!form[key]} aria-label={`${f.label} for ${p.name}`}
                              onClick={() => toggle(p.id, key)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
                                form[key] ? 'bg-cyan-500 border-cyan-500' : 'bg-slate-700 border-slate-600'
                              } ${changed ? 'ring-2 ring-cyan-300' : ''}`}>
                              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${form[key] ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          ) : (
                            <span className="text-slate-200 tabular-nums">{showValue(key, form[key])}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )),
              ];
            })}
          </tbody>
        </table>
      </div>

      {reviewing && (
        <section aria-labelledby="compare-review-title" className="rounded-xl border border-cyan-500/50 bg-slate-800 p-6 space-y-5">
          <h2 id="compare-review-title" className="text-lg font-semibold text-white">Review changes</h2>
          {changesByPlan.map(({ plan, changes }) => {
            const subs = subscriberCounts?.[plan.id];
            return (
              <div key={plan.id} role="group" aria-label={plan.name} className="space-y-2">
                <h3 className="text-base font-semibold text-white">{plan.name}</h3>
                <p className="text-sm text-amber-200">
                  {subs == null ? 'Every boat on this plan gets these changes at its next sync.'
                    : `${plural(subs, 'active subscription')} get${subs === 1 ? 's' : ''} these changes at their next sync.`}
                </p>
                <ul className="space-y-1 text-[15px] text-slate-300">
                  {changes.map((c) => (
                    <li key={c.key}><span className="text-slate-100">{c.label}:</span> {c.from} → <span className="text-cyan-300">{c.to}</span></li>
                  ))}
                </ul>
              </div>
            );
          })}
          <div>
            <label htmlFor="compare-reason" className="block text-[15px] font-medium text-slate-100">Reason (optional)</label>
            <p className="text-sm text-slate-400">Kept in the history of each plan saved.</p>
            <textarea id="compare-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-2 w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none" />
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" disabled={saving} onClick={save}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50">
              {saving ? 'Saving...' : `Save ${plural(changesByPlan.length, 'plan')}`}
            </button>
            <button type="button" onClick={() => setReviewing(false)} className="px-4 py-2 text-slate-300 hover:text-white">Keep editing</button>
          </div>
        </section>
      )}

      <div className="sticky bottom-0 z-40 flex flex-wrap items-center gap-3 rounded-xl border border-slate-700 bg-slate-900/95 px-4 py-3 shadow-2xl backdrop-blur">
        <p className="basis-full sm:basis-auto mr-auto text-[15px] text-slate-300">
          {changeCount === 0 ? 'No unsaved changes'
            : <span className="text-white">{`${plural(changeCount, 'unsaved change')} in ${plural(changesByPlan.length, 'plan')}`}</span>}
        </p>
        <button type="button" disabled={changeCount === 0} onClick={() => setEdits({})}
          className="ml-auto sm:ml-0 px-4 py-2 text-slate-300 hover:text-white disabled:opacity-40">
          Discard
        </button>
        {!reviewing && (
          <button type="button" disabled={changeCount === 0} onClick={() => setReviewing(true)}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50">
            Review &amp; save
          </button>
        )}
      </div>
    </div>
  );
}
